import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_BASE_URL } from "../../../../config/config";
import Header from "../../../Header/index";
import Footer from "../../../footer";
import Video from "../../../../assets/img/welcome.mp4";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";

const ProjectsShowMore = () => {
  const { id } = useParams();
  const location = useLocation();
  const section = location.state?.section || "projects";

  const [itemData, setItemData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [, setError] = useState(null);

  const { t, i18n } = useTranslation();

  const languageMap = { tj: 1, ru: 2, en: 3 };
  const getLanguageCode = (lang) => languageMap[lang] || 2;
  const authHeaders = { accept: "text/plain", "api-version": "1.0" };

  useEffect(() => {
    if (!id) {
      setError("No item ID provided");
      setLoading(false);
      return;
    }

    const fetchItemData = async () => {
      try {
        setLoading(true);
        setError(null);
        setImageError(false);

        const langCode = getLanguageCode(i18n.language);
        const url = `${API_BASE_URL}/api/events/${id}?lang=${langCode}`;

        const response = await axios.get(url, { headers: authHeaders });
        setItemData(response.data);
      } catch (err) {
        console.error("Error fetching item details:", err);
        setError(t("Failed to load item details"));
      } finally {
        setLoading(false);
      }
    };

    fetchItemData();
  }, [id, i18n.language]);

  const handleImageError = (imageUrl) => {
    console.log("Image failed to load:", imageUrl);
    setImageError(true);
  };

  const getPdfUrl = (item, lang) => {
    if (!item) return null;

    if (lang === "ru") return item.pdFfileRu || item.pdFfile;
    if (lang === "en") return item.pdFfileEn || item.pdFfile;
    if (lang === "tj") return item.pdFfileTj || item.pdFfile;

    return item.pdFfile;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="backgroundVideo22 backSmall">
          <video autoPlay loop muted className="background-video">
            <source src={Video} type="video/mp4" />
            {t("Your browser does not support the video tag.")}
          </video>
          <div className="video-overlay"></div>
        </div>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary mb-4"></div>
            <p className="text-muted fs-6">{t("Loading...")}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!itemData) {
    return (
      <>
        <Header />
        <div className="container py-5">
          <div className="text-center">
            <p className="text-muted">{t("No data available")}</p>
            <Link to="/maps" className="btn btn-primary">
              {t("discover.back")}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pdfUrl = getPdfUrl(itemData, i18n.language);

  return (
    <>
      <div className="position-relative white-text" style={{ height: "200px" }}>
        <video
          autoPlay
          loop
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        >
          <source src={Video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 5000,
          }}
        >
          <Header />
        </div>
      </div>
      <main>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-11">
              <div className="card border-0 custom-card">
                <div className="card-body p-0">
                  {itemData.image && (
                    <div className="custom-image-container">
                      {!imageError ? (
                        <img
                          key={itemData.image}
                          src={itemData.image}
                          className="custom-image"
                          alt="oops"
                          onError={() => handleImageError(itemData.image)}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center bg-light border rounded"
                          style={{
                            height: "300px",
                            minHeight: "200px",
                          }}
                        >
                          <div className="text-center">
                            <i
                              className="bi bi-image text-muted"
                              style={{ fontSize: "3rem" }}
                            ></i>
                            <div className="mt-2">
                              <span className="text-muted">
                                {t("Image not available") ||
                                  "Image not available"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-4 p-lg-5">
                    <div className="row">
                      <div className="col-lg-8">
                        <h1
                          className="display-5 fw-bold text-dark mb-4"
                          dangerouslySetInnerHTML={{ __html: itemData.title }}
                        ></h1>
                        {itemData.shortDesc && (
                          <div className="mb-4">
                            <p
                              className="lead"
                              dangerouslySetInnerHTML={{
                                __html: itemData.shortDesc,
                              }}
                            ></p>
                          </div>
                        )}
                        {itemData.longDesc && (
                          <div className="mb-4">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: itemData.longDesc,
                              }}
                            ></div>
                          </div>
                        )}
                        {pdfUrl && (
                          <div className="mb-4">
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="custom-btn btn-primary"
                            >
                              <i className="bi bi-file-earmark-pdf"></i>{" "}
                              {t("viewPDF")}
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="col-lg-4">
                        <div className="sticky-sidebar">
                          <div className="custom-details-box p-4">
                            {(itemData.createdDate || itemData.date) && (
                              <div className="mb-3">
                                <strong className="d-block mb-2">
                                  {t("date")}
                                </strong>
                                <span className="text-dark">
                                  {new Date(
                                    itemData.createdDate || itemData.date
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            <div className="mt-4 pt-3">
                              <Link to="/maps" className="custom-btn w-100">
                                <i className="bi bi-arrow-left"></i>{" "}
                                {t("discover.back")}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ProjectsShowMore;
